import { Link } from "react-router-dom";
import { useParallax } from "@/hooks/useParallax";

interface InternalCTASectionProps {
  tag: string;
  title: string;
  highlight: string;
  subtitle: string;
  buttonLabel: string;
  homeButtonLabel: string;
  onQuote: () => void;
  image: string;
}

/**
 * Unified CTA section for every internal page.
 * Keeps the same overlay opacity, gradient and parallax image treatment
 * so all internal CTAs look identical across the site.
 */
export default function InternalCTASection({
  tag,
  title,
  highlight,
  subtitle,
  buttonLabel,
  homeButtonLabel,
  onQuote,
  image,
}: InternalCTASectionProps) {
  const { ref: bgRef, offset } = useParallax(0.14);

  return (
    <section id="cta" className="relative py-16 md:py-24 bg-[#0B1628] overflow-hidden">
      {/* Unified humanized background with subtle parallax */}
      <div ref={bgRef} className="absolute inset-0 overflow-hidden">
        <img
          src={image}
          alt=""
          aria-hidden="true"
          className="absolute -top-[12%] left-0 w-full h-[124%] object-cover object-center"
          style={{ transform: `translateY(${offset}px)` }}
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-[#0B1628]/86" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1628] via-[#0B1628]/68 to-[#0B1628]" />
      </div>

      {/* Top transition for a soft joint with the section above */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-[#0A1220] to-transparent z-[5] pointer-events-none" />

      <div
        className="absolute inset-0 pointer-events-none opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(74,125,191,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(74,125,191,0.4) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary-500/8 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full px-4 sm:px-6 lg:px-10 relative z-10 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary-400/20 bg-primary-500/8 mb-5">
          <span className="w-2 h-2 rounded-full bg-primary-400 animate-pulse" />
          <span className="text-[10px] font-label font-semibold text-primary-300 uppercase tracking-[0.2em]">
            {tag}
          </span>
        </div>
        <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-3">
          {title}{" "}
          <span className="bg-gradient-to-r from-primary-300 to-primary-500 bg-clip-text text-transparent">
            {highlight}
          </span>
        </h2>
        <p className="text-sm text-foreground-400/60 max-w-lg mx-auto mb-8 leading-relaxed">
          {subtitle}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={onQuote}
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gradient-to-r from-[#4A7DBF] to-[#3A6DAF] text-white font-label font-semibold text-sm hover:from-[#5A8DCF] hover:to-[#4A7DBF] transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-chat-smile-3-line" />
            {buttonLabel}
          </button>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-primary-400/20 bg-primary-500/8 text-primary-300 font-label font-semibold text-sm hover:border-primary-400/40 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line" />
            {homeButtonLabel}
          </Link>
        </div>
      </div>
    </section>
  );
}